import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Radio,
  Wand2,
  Activity,
  MonitorSmartphone,
  Sprout,
  Beer,
  UtensilsCrossed,
  LayoutDashboard,
  Workflow,
  ShieldAlert,
  TrendingUp,
  Shield,
  Linkedin,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import ChuckHeadshot from "@/assets/team/Chuck_Headshot.jpeg";
import JimHeadshot from "@/assets/team/Jim_Headshot.jpeg";
// import TylerHeadshot from "@/assets/team/Tyler_Headshot.jpeg"; // Temporarily hidden
import JimmyHeadshot from "@/assets/team/Jimmy_Headshot.jpeg";
import { useResponsiveInfo } from "@/hooks/useResponsiveViewport";
import { PilotContactModal, ThankYouModal } from "@/components/pilot";
import { DemoModal } from "@/components/demo";
import { LandingFooter } from "@/components/layout/LandingFooter";
import { PublicNavigation } from "@/components/layout/PublicNavigation";
import { SEO } from "@/components/seo/SEO";

// ============================================================================
// DESIGN TOKENS (Style Guide Colors)
// ============================================================================
const colors = {
  primary: "hsl(var(--primary))",
  primaryHover: "hsl(var(--primary) / 0.9)",
  secondary: "#B2AC88", // Sage Green
  seafoam: "#93E9BE",
  blueGrotto: "#116B98",
  desertSun: "#E27D3D",
  charcoal: "#0A0A0A",
  midGray: "#6E6E6E",
  lightGray: "#F4F5F4",
  champagne: "#F7E7CE",
  white: "#FFFFFF",
};

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const timelineItemVariants = {
  hidden: { opacity: 0, x: -30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5 } },
};

// ============================================================================
// DATA
// ============================================================================
const whatWeDo = [
  {
    icon: Radio,
    title: "IoT Sensing & Control",
    description:
      "Connect soil moisture probes, tank temperature sensors, current clamps, HVAC controllers, and cameras into one coherent system. Different sensors, different actions, one platform coordinating it all.",
    color: colors.primary,
    colorLight: "hsl(var(--primary) / 0.08)",
  },
  {
    icon: Wand2,
    title: "AI-Powered Visual Editor",
    description:
      "A no-code, visual environment where operators can build dashboards, automations, and alerts using natural language and drag-and-drop instead of complex scripting.",
    color: colors.blueGrotto,
    colorLight: `${colors.blueGrotto}15`,
  },
  {
    icon: Activity,
    title: "Real-Time Monitoring & Automation",
    description:
      "Live views of everything that matters, from tanks, pumps, and valves to compressors, fridges, and HVAC zones, with automated workflows that respond instantly when conditions change.",
    color: colors.desertSun,
    colorLight: `${colors.desertSun}15`,
  },
  {
    icon: MonitorSmartphone,
    title: "Multi-Device Access",
    description:
      "A desktop portal and mobile apps give teams the same live data and controls whether they are in the control room, on the production floor, or out in the field.",
    color: colors.seafoam,
    colorLight: `${colors.seafoam}15`,
  },
];

const timeline = [
  {
    icon: Sprout,
    title: "First Pilot: Agriculture",
    year: "2025",
    description:
      "Our first deployment was an 8-acre cherry farm where we installed soil moisture sensors and controller boards to automate irrigation. This proved we could deliver precision water management and real-time visibility using our own IoT hardware and software stack.",
    color: colors.primary,
    colorLight: "hsl(var(--primary) / 0.08)",
  },
  {
    icon: Beer,
    title: "Expansion to Brewing",
    year: "2025",
    description:
      "Next, we designed a system for breweries to monitor fermentation tanks, automate glycol valve control, log batch history, and catch faults before they ruin product.",
    color: colors.blueGrotto,
    colorLight: `${colors.blueGrotto}15`,
  },
  {
    icon: UtensilsCrossed,
    title: "Expansion to Restaurants",
    year: "2025",
    description:
      "We then extended the platform to restaurant operations: monitoring appliance health, environmental conditions, and using computer vision to understand customer flow, table turns, and store performance in real time.",
    color: colors.desertSun,
    colorLight: `${colors.desertSun}15`,
  },
];

const operatorBenefits = [
  {
    icon: LayoutDashboard,
    title: "See Everything in One Place",
    description:
      "Replace scattered systems and clipboards with unified dashboards that show what is happening right now and what changed over time.",
  },
  {
    icon: Workflow,
    title: "Automate Repetitive Work",
    description:
      'Turn "if X happens, do Y" into simple, visual workflows. From soil moisture triggers to equipment fault alerts.',
  },
  {
    icon: ShieldAlert,
    title: "Catch Problems Early",
    description:
      "Use real-time thresholds and historical trends to spot stuck valves, failing compressors, or temperature drift before downtime.",
  },
  {
    icon: TrendingUp,
    title: "Make Data-Driven Decisions",
    description:
      "Use historical batch data, irrigation logs, energy usage, and customer behavior analytics to fine-tune processes.",
  },
];

const approachPrinciples = [
  {
    title: "Vertical Insight, Horizontal Platform",
    description:
      "We go deep in key verticals like agriculture, brewing, and restaurants, but the underlying platform is flexible enough to support any sensor-rich environment.",
    span: 2,
    color: colors.primary,
    inverted: false,
  },
  {
    title: "Operator-First Design",
    description:
      "We assume our users are experts in water, brewing, or store operations, not in programming. The interface is built around their mental models, not ours.",
    span: 1,
    color: colors.blueGrotto,
    inverted: false,
  },
  {
    title: "Hardware-Aware, Not Hardware-Locked",
    description:
      "We support our own IoT devices as well as third-party sensors and controllers so customers can modernize without ripping out what already works.",
    span: 1,
    color: colors.desertSun,
    inverted: false,
  },
  {
    title: "Secure by Design",
    description:
      "From role-based access control and audit logging to encrypted communications, we treat operational data like the critical asset it is.",
    span: 2,
    color: colors.primary,
    inverted: true,
    icon: Shield,
  },
];

const team = [
  {
    name: "Charles Wingate",
    role: "Co-Founder & CEO",
    initials: "CW",
    description:
      "Charles leads vision, strategy, and go-to-market, working closely with customers to shape the roadmap around real-world needs.",
    image: ChuckHeadshot,
    linkedinUrl: "https://www.linkedin.com/in/charleswingate/",
  },
  {
    name: "Jim Garcia",
    role: "Co-Founder & CTO",
    initials: "JG",
    description:
      "Jim oversees the platform architecture, from IoT hardware and edge processing to the AI and no-code editor that power TerraMatrix.",
    image: JimHeadshot,
    linkedinUrl: "https://www.linkedin.com/in/jim-garcia-6316853/",
  },
  {
    name: "Jimmy Garcia",
    role: "Co-Founder & CPO",
    initials: "JG",
    description:
      "Jimmy drives product design and UX, ensuring that complex capabilities are accessible through intuitive, operator-friendly experiences.",
    image: JimmyHeadshot,
    linkedinUrl: "https://www.linkedin.com/in/jamesgarcia4/",
  },
  // Tyler Garcia temporarily hidden
];

// ============================================================================
// HERO SECTION (with Mission Statement)
// ============================================================================
const HeroSection: React.FC = () => {
  return (
    <section
      className="relative min-h-[70vh] flex items-center justify-center overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.charcoal} 100%)`,
      }}
    >
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 md:py-40">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="text-center max-w-4xl mx-auto"
        >
          <motion.p
            variants={fadeInUp}
            className="text-sm md:text-base font-medium uppercase tracking-wider mb-6"
            style={{ color: colors.seafoam }}
          >
            Our Mission
          </motion.p>

          <motion.h1
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-2xl md:text-3xl lg:text-4xl font-medium text-white leading-relaxed mb-8"
          >
            To modernize farms, breweries, and small businesses with enterprise-level automation and operational intelligence.
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
          >
            We believe that if you can measure it, you should be able to automate it. TerraMatrix
            exists to turn raw sensor data into clear insight and effortless action.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// INTRO SECTION
// ============================================================================
const IntroSection: React.FC = () => {
  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-6"
            style={{ color: colors.charcoal }}
          >
            About TerraMatrix
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-lg md:text-xl leading-relaxed mb-4"
            style={{ color: colors.midGray }}
          >
            TerraMatrix is building the operating system for real-world infrastructure: an
            AI-powered visual editor and IoT automation platform that connects the physical and
            digital worlds.
          </motion.p>
          <motion.p
            variants={fadeInUp}
            className="text-lg leading-relaxed"
            style={{ color: colors.midGray }}
          >
            We help teams see every critical process in one place, automate the work that should not
            be manual, and make smarter decisions from the data their operations are already
            generating.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// WHAT WE DO SECTION
// ============================================================================
const WhatWeDoSection: React.FC = () => {
  return (
    <section className="py-20 md:py-28" style={{ backgroundColor: colors.lightGray }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: colors.charcoal }}
          >
            What We Do
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg max-w-2xl mx-auto" style={{ color: colors.midGray }}>
            TerraMatrix unifies sensing, control, and AI analytics on a single platform so teams can
            manage complex, sensor-rich environments without writing code.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-8"
        >
          {whatWeDo.map((item, index) => (
            <motion.div
              key={item.title}
              variants={fadeInUp}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
              className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300"
            >
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                style={{ backgroundColor: item.colorLight }}
              >
                <item.icon className="h-8 w-8" style={{ color: item.color }} />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: colors.charcoal }}>
                {item.title}
              </h3>
              <p className="leading-relaxed" style={{ color: colors.midGray }}>
                {item.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// ORIGIN STORY SECTION (Timeline)
// ============================================================================
const OriginStorySection: React.FC = () => {
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: colors.charcoal }}
          >
            How We Started
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-lg max-w-3xl mx-auto leading-relaxed"
            style={{ color: colors.midGray }}
          >
            TerraMatrix began with a simple question:{" "}
            <span className="font-medium" style={{ color: colors.charcoal }}>
              Why are the systems that run our physical world still so fragmented, manual, and
              opaque?
            </span>
          </motion.p>
        </motion.div>

        {/* Timeline */}
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="relative"
          >
            {/* Timeline line */}
            <div
              className="absolute left-4 md:left-6 top-0 bottom-0 w-0.5"
              style={{ backgroundColor: "hsl(var(--primary) / 0.19)" }}
            />

            {timeline.map((item, index) => (
              <motion.div
                key={item.title}
                variants={timelineItemVariants}
                transition={{ delay: index * 0.2 }}
                className="relative pl-12 md:pl-16 pb-12 last:pb-0"
              >
                {/* Timeline dot */}
                <div
                  className="absolute left-2 md:left-4 w-4 h-4 rounded-full border-4 border-white"
                  style={{ backgroundColor: item.color }}
                />

                {/* Card */}
                <div
                  className="bg-white rounded-xl p-6 shadow-sm border-l-4 hover:shadow-md transition-shadow"
                  style={{ borderLeftColor: item.color }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: item.colorLight }}
                    >
                      <item.icon className="h-5 w-5" style={{ color: item.color }} />
                    </div>
                    <span
                      className="text-sm font-semibold uppercase tracking-wider"
                      style={{ color: item.color }}
                    >
                      {item.year}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: colors.charcoal }}>
                    {item.title}
                  </h3>
                  <p className="leading-relaxed" style={{ color: colors.midGray }}>
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Closing statement */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="text-center text-lg mt-12 font-medium"
            style={{ color: colors.charcoal }}
          >
            The pattern across these environments is the same: different sensors, different actions,
            the same TerraMatrix AI engine behind the scenes.
          </motion.p>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// OPERATOR BENEFITS SECTION
// ============================================================================
const OperatorBenefitsSection: React.FC = () => {
  return (
    <section className="py-20 md:py-28" style={{ backgroundColor: colors.champagne }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: colors.charcoal }}
          >
            How TerraMatrix Helps Operators
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg max-w-2xl mx-auto" style={{ color: colors.midGray }}>
            We design TerraMatrix for the people who live with these systems every day: farm
            managers, brewmasters, facilities managers, and operations leaders.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {operatorBenefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              variants={fadeInUp}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
              className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300"
            >
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                style={{ backgroundColor: "hsl(var(--primary) / 0.06)" }}
              >
                <benefit.icon className="h-6 w-6" style={{ color: colors.primary }} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: colors.charcoal }}>
                {benefit.title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: colors.midGray }}>
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// APPROACH SECTION (Bento Grid)
// ============================================================================
const ApproachSection: React.FC = () => {
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: colors.charcoal }}
          >
            Our Approach
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg max-w-2xl mx-auto" style={{ color: colors.midGray }}>
            A few principles guide how we build and deploy TerraMatrix.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-6"
        >
          {approachPrinciples.map((principle, index) => {
            const isInverted = principle.inverted;
            const colSpan =
              principle.span === 2
                ? "md:col-span-2"
                : principle.span === 3
                  ? "md:col-span-3"
                  : "";

            return (
              <motion.div
                key={principle.title}
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className={`rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all duration-300 ${colSpan}`}
                style={{
                  backgroundColor: isInverted ? principle.color : colors.lightGray,
                }}
              >
                <div
                  className={`flex ${isInverted ? "flex-col md:flex-row items-center gap-6" : "flex-col"}`}
                >
                  {isInverted && principle.icon && (
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 bg-white/20">
                      <principle.icon className="h-8 w-8 text-white" />
                    </div>
                  )}
                  <div className={isInverted ? "text-center md:text-left" : ""}>
                    <h3
                      className={`text-xl font-bold mb-3 ${isInverted ? "text-white" : ""}`}
                      style={{ color: isInverted ? undefined : colors.charcoal }}
                    >
                      {principle.title}
                    </h3>
                    <p
                      className={`leading-relaxed ${isInverted ? "text-white/90" : ""}`}
                      style={{ color: isInverted ? undefined : colors.midGray }}
                    >
                      {principle.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// TEAM SECTION
// ============================================================================
const TeamSection: React.FC = () => {
  return (
    <section className="py-20 md:py-28" style={{ backgroundColor: colors.lightGray }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: colors.charcoal }}
          >
            The Team
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg max-w-2xl mx-auto" style={{ color: colors.midGray }}>
            TerraMatrix is led by a founding team that blends deep technical experience with
            hands-on work in farms, industrial systems, and complex infrastructure.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {team.map((member, index) => (
            <motion.article
              key={member.name}
              variants={fadeInUp}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-lg transition-all duration-300"
            >
              {/* Avatar */}
              <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to initials if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = `<div class="w-full h-full flex items-center justify-center text-2xl font-semibold" style="background-color: hsl(var(--primary) / 0.13); color: hsl(var(--primary))">${member.initials}</div>`;
                    }
                  }}
                />
              </div>

              {/* Name */}
              <h3 className="text-xl font-semibold" style={{ color: colors.charcoal }}>
                {member.name}
              </h3>

              {/* Role */}
              <p
                className="text-sm font-medium uppercase tracking-wider mb-4"
                style={{ color: colors.primary }}
              >
                {member.role}
              </p>

              {/* LinkedIn */}
              <a
                href={member.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors"
                aria-label={`View ${member.name}'s LinkedIn profile`}
              >
                <Linkedin className="h-5 w-5" style={{ color: colors.midGray }} />
              </a>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// WHAT'S NEXT SECTION
// ============================================================================
const WhatsNextSection: React.FC = () => {
  return (
    <section className="pt-20 pb-32 md:pt-28 md:pb-40 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-6"
            style={{ color: colors.charcoal }}
          >
            What's Next
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-lg md:text-xl leading-relaxed mb-6"
            style={{ color: colors.midGray }}
          >
            We are continuing to expand TerraMatrix across new sites and new verticals, from more
            farms and breweries to restaurant chains and other infrastructure-heavy operations.
          </motion.p>
          <motion.p
            variants={fadeInUp}
            className="text-lg leading-relaxed font-medium"
            style={{ color: colors.charcoal }}
          >
            If you are responsible for real-world systems and want to bring AI, automation, and
            visibility to your operation, we would love to talk.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// CTA SECTION
// ============================================================================
interface CTASectionProps {
  onContactClick: () => void;
  onDemoClick: () => void;
}

const CTASection: React.FC<CTASectionProps> = ({ onContactClick, onDemoClick }) => {
  return (
    <section className="pt-16 pb-20 md:pt-20 md:pb-28" style={{ backgroundColor: colors.primary }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to See It in Action?
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-white/85 mb-8 max-w-xl mx-auto">
            Let's talk about what TerraMatrix can do for your operation. No pressure, just a conversation.
          </motion.p>
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="text-base font-semibold min-w-[160px] px-6 py-5 rounded-xl border-2 shadow-lg hover:shadow-xl bg-background hover:bg-accent border-background text-primary transition-all duration-200"
              onClick={onContactClick}
            >
              Get in Touch
            </Button>
            <Button
              size="lg"
              variant="ghost"
              className="text-base font-semibold min-w-[160px] px-6 py-5 rounded-xl border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 transition-all duration-200"
              onClick={onDemoClick}
            >
              Watch Demo
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// MAIN ABOUT PAGE COMPONENT
// ============================================================================
export const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsiveInfo();

  // Scroll state
  const [scrolled, setScrolled] = useState(false);

  // Modal states
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [thankYouModalOpen, setThankYouModalOpen] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle contact button click
  const handleContactClick = () => {
    if (isMobile) {
      navigate("/contact");
    } else {
      setContactModalOpen(true);
    }
  };

  // Handle successful form submission
  const handleContactSuccess = (email: string) => {
    setSubmittedEmail(email);
    setContactModalOpen(false);
    setThankYouModalOpen(true);
  };

  // Handle thank you modal close
  const handleThankYouClose = () => {
    setThankYouModalOpen(false);
    setSubmittedEmail("");
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="About TerraMatrix - Our Mission, Team & Approach"
        description="Learn about TerraMatrix's mission to bring AI, automation, and visibility to physical operations. Meet our founding team and discover our approach to IoT sensor systems for agriculture, brewing, and restaurant industries."
        canonical="/about"
        keywords="about terramatrix, IoT platform company, industrial automation team, agriculture technology, restaurant analytics company, brewing sensors"
      />
      {/* Skip link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-white focus:text-primary focus:rounded focus:shadow-lg focus:font-medium"
      >
        Skip to main content
      </a>
      <PublicNavigation scrolled={scrolled} onContactClick={handleContactClick} />
      <main id="main-content">
        <HeroSection />
        <IntroSection />
        {/* Hidden sections - uncomment to restore
        <WhatWeDoSection />
        <OriginStorySection />
        <OperatorBenefitsSection />
        <ApproachSection />
        */}
        <TeamSection />
        <WhatsNextSection />

        {/* Curved transition to CTA */}
        <div className="relative">
          <svg
            className="absolute top-0 left-0 w-full h-24 -translate-y-full text-primary"
            viewBox="0 0 1440 96"
            preserveAspectRatio="none"
            fill="none"
          >
            <path
              d="M0 96L60 85.3C120 75 240 53 360 48C480 43 600 53 720 58.7C840 64 960 64 1080 58.7C1200 53 1320 43 1380 37.3L1440 32V96H1380C1320 96 1200 96 1080 96C960 96 840 96 720 96C600 96 480 96 360 96C240 96 120 96 60 96H0Z"
              fill="currentColor"
            />
          </svg>
          <CTASection onContactClick={handleContactClick} onDemoClick={() => setDemoModalOpen(true)} />
        </div>
      </main>
      <LandingFooter />

      {/* Contact Modal */}
      <PilotContactModal
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        onSuccess={handleContactSuccess}
      />

      {/* Thank You Modal */}
      <ThankYouModal isOpen={thankYouModalOpen} onClose={handleThankYouClose} email={submittedEmail} />

      {/* Demo Modal */}
      <DemoModal isOpen={demoModalOpen} onClose={() => setDemoModalOpen(false)} />
    </div>
  );
};
